import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useOutletContext } from 'react-router-dom';
import Icon from '../components/Icon';
import { useAuth } from '../contexts/AuthContext';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { fetchComplianceFlags, patchComplianceFlag } from '../services/api';
import { corridorMetadata, timeAgo } from '../utils/format';

const SEVERITY_BADGE = {
  critical: 'badge-danger',
  high:     'badge-warning',
  medium:   'badge-info',
  low:      'badge-muted',
};

const STATUS_BADGE = {
  open:      'badge-danger',
  reviewing: 'badge-warning',
  escalated: 'badge-danger',
  dismissed: 'badge-muted',
  str_filed: 'badge-info',
};

const FLAG_TYPE_LABEL = {
  large_transaction: 'Large transaction',
  structuring:       'Structuring',
  velocity:          'Velocity',
  sanctions_match:   'Sanctions match',
  unusual_pattern:   'Unusual pattern',
};

const Compliance = () => {
  const { environment } = useOutletContext();
  const { profile } = useAuth();
  const { isSuperAdmin } = usePagePermissions();
  // Admin actions on flags (review/escalate/dismiss/file-STR) are gated on
  // super-admin. Accept both 'super_admin' (current) and legacy 'admin' via
  // the hook so a profile created before the role enum was extended still works.
  const isAdmin = isSuperAdmin;

  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('queue');

  // Re-fetch on env change. Note: compliance flags are joined to transactions
  // server-side so they implicitly inherit env via the join, even though the
  // flags table itself isn't env-tagged.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchComplianceFlags({ limit: 100 })
      .then((data) => {
        if (cancelled) return;
        setFlags(data || []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load flags');
        setFlags([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [environment]);

  const filtered = useMemo(() => {
    return flags.filter((f) => {
      if (activeTab === 'queue' && (f.status === 'dismissed' || f.status === 'str_filed')) return false;
      if (activeTab === 'str' && f.status !== 'str_filed') return false;
      if (activeTab === 'archive' && f.status !== 'dismissed') return false;
      if (severityFilter !== 'all' && f.severity !== severityFilter) return false;
      if (statusFilter !== 'all' && f.status !== statusFilter) return false;
      return true;
    });
  }, [flags, severityFilter, statusFilter, activeTab]);

  const counts = useMemo(() => {
    return flags.reduce(
      (acc, f) => {
        if (f.status === 'open') acc.open += 1;
        if (f.status === 'reviewing') acc.reviewing += 1;
        if (f.status === 'escalated') acc.escalated += 1;
        if (f.severity === 'critical' && f.status !== 'dismissed' && f.status !== 'str_filed') acc.critical += 1;
        if (f.status === 'str_filed') acc.str += 1;
        return acc;
      },
      { open: 0, reviewing: 0, escalated: 0, critical: 0, str: 0 }
    );
  }, [flags]);

  const updateStatus = async (id, nextStatus) => {
    // Optimistic update — flip the row immediately, roll back on failure.
    const prev = flags;
    setFlags((p) => p.map((f) => (f.id === id ? { ...f, status: nextStatus } : f)));
    try {
      await patchComplianceFlag(id, { status: nextStatus, reviewed_by: profile?.email });
    } catch (err) {
      setFlags(prev);
      setError(`Failed to update flag: ${err.message}`);
    }
  };

  const TABS = [
    { id: 'queue',   label: 'Active queue', count: counts.open + counts.reviewing + counts.escalated },
    { id: 'str',     label: 'STR filed',    count: counts.str },
    { id: 'archive', label: 'Archived',     count: flags.filter((f) => f.status === 'dismissed').length },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Compliance</h1>
          <p>
            AML flags, FINTRAC reporting queue, and review actions
            <span className="text-tx3"> · {environment}</span>
          </p>
        </div>
      </div>

      <div className="dashboard-grid stagger mb-4">
        <div className="card">
          <div className="card-title">Open</div>
          <div className="stat-value" style={{ color: 'var(--err)' }}>{counts.open}</div>
          <div className="stat-label">Awaiting review</div>
        </div>
        <div className="card">
          <div className="card-title">Reviewing</div>
          <div className="stat-value" style={{ color: 'var(--warn)' }}>{counts.reviewing}</div>
          <div className="stat-label">In progress</div>
        </div>
        <div className="card">
          <div className="card-title">Critical</div>
          <div
            className="stat-value"
            style={{
              color: counts.critical > 0 ? 'var(--err)' : 'var(--ok)',
              animation: counts.critical > 0 ? 'notifPulse 1.6s var(--ease) infinite' : 'none',
            }}
          >
            {counts.critical}
          </div>
          <div className="stat-label">Severity = critical</div>
        </div>
        <div className="card">
          <div className="card-title">STR Filed</div>
          <div className="stat-value" style={{ color: 'var(--info)' }}>{counts.str}</div>
          <div className="stat-label">FINTRAC submissions</div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="row" data-tabs="true" style={{ gap: 4, borderBottom: '1px solid var(--bd)', marginBottom: 16 }}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 16px',
                background: 'transparent',
                color: activeTab === tab.id ? 'var(--teal)' : 'var(--tx2)',
                fontWeight: activeTab === tab.id ? 700 : 500,
                fontSize: 13,
                borderBottom: activeTab === tab.id ? '2px solid var(--teal)' : '2px solid transparent',
                marginBottom: -1,
                cursor: 'pointer',
                transition: 'color var(--tf) var(--ease)',
              }}
            >
              {tab.label}
              <span className="badge badge-muted" style={{ marginLeft: 8 }}>{tab.count}</span>
            </button>
          ))}
        </div>

        <div className="row row-gap-3 mb-3" style={{ flexWrap: 'wrap' }}>
          <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} aria-label="Filter by severity" style={selectStyle}>
            <option value="all">All severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status" style={selectStyle}>
            <option value="all">All statuses</option>
            <option value="open">Open</option>
            <option value="reviewing">Reviewing</option>
            <option value="escalated">Escalated</option>
            <option value="dismissed">Dismissed</option>
            <option value="str_filed">STR filed</option>
          </select>
        </div>

        {error && (
          <div role="alert" style={{ background: 'var(--err-bg)', color: 'var(--err)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13, marginBottom: 'var(--s3)' }}>
            {error}
          </div>
        )}

        {loading && flags.length === 0 ? (
          <div className="col col-gap-2">
            {[0, 1, 2].map((i) => <div key={i} className="skeleton skeleton-block" style={{ height: 60 }} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty">
            <div className="empty-icon"><Icon name="shield" size={26} /></div>
            <h3>No flags in this view</h3>
            <p>{flags.length === 0 ? `No flags exist on ${environment} yet.` : "You're all caught up."}</p>
          </div>
        ) : (
          <div className="stagger" style={{ display: 'grid', gap: 8 }}>
            {filtered.map((flag) => (
              <FlagRow key={flag.id} flag={flag} isAdmin={isAdmin} onUpdate={updateStatus} />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

const FlagRow = ({ flag, isAdmin, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const sevColor = flag.severity === 'critical' ? 'var(--err)'
    : flag.severity === 'high' ? 'var(--warn)'
    : flag.severity === 'medium' ? 'var(--teal)'
    : 'var(--tx3)';

  return (
    <div
      className="flag-row"
      style={{ flexDirection: 'column', alignItems: 'stretch', borderLeftColor: sevColor, cursor: 'pointer' }}
      onClick={() => setOpen((v) => !v)}
    >
      <div className="row row-gap-3" style={{ alignItems: 'center' }}>
        <span className={clsx('badge', SEVERITY_BADGE[flag.severity])} style={{ minWidth: 60, textAlign: 'center' }}>
          {flag.severity}
        </span>
        <div className="col col-gap-2 flex-1">
          <div style={{ color: 'var(--tx1)', fontWeight: 600, fontSize: 13 }}>{flag.title}</div>
          <div className="text-tx3 text-xs">
            {FLAG_TYPE_LABEL[flag.flag_type] || flag.flag_type}
            {flag.corridor ? ` • ${corridorMetadata[flag.corridor]?.flag || ''} ${flag.corridor}` : ''}
            {flag.rule_triggered ? <> • <span style={{ color: 'var(--tx2)' }}>{flag.rule_triggered}</span></> : null}
          </div>
        </div>
        <span className={clsx('badge', STATUS_BADGE[flag.status] || 'badge-muted')}>
          {(flag.status || '').replace('_', ' ')}
        </span>
        <span className="text-tx3 text-xs" style={{ minWidth: 70, textAlign: 'right' }}>
          {timeAgo(flag.created_at)}
        </span>
      </div>

      {open && (
        <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--bd)' }}>
          <div className="row row-gap-4 mb-3" style={{ flexWrap: 'wrap' }}>
            <div className="col col-gap-2">
              <div className="text-tx3 text-xs">Sender</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--tx1)', wordBreak: 'break-all' }}>{flag.sender_address || '—'}</div>
            </div>
            <div className="col col-gap-2">
              <div className="text-tx3 text-xs">Created</div>
              <div style={{ color: 'var(--tx1)', fontSize: 12 }}>{flag.created_at ? new Date(flag.created_at).toLocaleString() : '—'}</div>
            </div>
            <div className="col col-gap-2">
              <div className="text-tx3 text-xs">Detected by</div>
              <div style={{ color: 'var(--tx1)', fontSize: 12 }}>{flag.rule_triggered || '—'}</div>
            </div>
          </div>

          {isAdmin && flag.status !== 'dismissed' && flag.status !== 'str_filed' && (
            <div className="row row-gap-2" style={{ flexWrap: 'wrap' }}>
              {flag.status === 'open' && (
                <button type="button" className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => onUpdate(flag.id, 'reviewing')}>
                  Mark reviewing
                </button>
              )}
              <button type="button" className="btn btn-outline" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => onUpdate(flag.id, 'escalated')}>
                Escalate
              </button>
              <button type="button" className="btn btn-primary" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => onUpdate(flag.id, 'str_filed')}>
                File STR
              </button>
              <button type="button" className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => onUpdate(flag.id, 'dismissed')}>
                Dismiss
              </button>
            </div>
          )}

          {!isAdmin && (
            <div className="text-tx3 text-xs">Admin role required to take action on flags.</div>
          )}
        </div>
      )}
    </div>
  );
};

const selectStyle = {
  padding: '10px 14px',
  background: 'var(--bg-elevated)',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--r-md)',
  color: 'var(--tx1)',
  fontSize: 13,
  fontFamily: 'var(--font)',
  cursor: 'pointer',
  outline: 'none',
};

export default Compliance;
